import { features } from '@ui/components/Features';
import type { StatsChartProps } from '@ui/components/StatsCharts/StatsChart';
import { useStatsAverageDailyVolume } from '@ui/hooks/stats/useStatsAverageDailyVolume';
import { useStatsAverageStakeSize } from '@ui/hooks/stats/useStatsAverageStakeSize';
import { useStatsClaimedVno } from '@ui/hooks/stats/useStatsClaimedVno';
import { useStatsDailyActiveWallets } from '@ui/hooks/stats/useStatsDailyActiveWallets';
import { useStatsFountainPenalties } from '@ui/hooks/stats/useStatsFountainPenalties';
import { useStatsFountainUsdLocked } from '@ui/hooks/stats/useStatsFountainUsdLocked';
import { useStatsFountainVnoLocked } from '@ui/hooks/stats/useStatsFountainVnoLocked';
import { useStatsHelpersByCollection } from '@ui/hooks/stats/useStatsHelpersByCollection';
import { useStatsHelpersByGarden } from '@ui/hooks/stats/useStatsHelpersByGarden';
import { useStatsReservoirUsdLocked } from '@ui/hooks/stats/useStatsReservoirUsdLocked';
import { useStatsReservoirVnoLocked } from '@ui/hooks/stats/useStatsReservoirVnoLocked';
import { useStatsRewardsPaid } from '@ui/hooks/stats/useStatsRewardsPaid';
import { useStatsStakers } from '@ui/hooks/stats/useStatsStakers';
import { useStatsStakingTvl } from '@ui/hooks/stats/useStatsStakingTvl';
import { useStatsTvlTokens } from '@ui/hooks/stats/useStatsTvlTokens';
import { useStatsTvlUsd } from '@ui/hooks/stats/useStatsTvlUsd';
import {
  useStatsVnoFountainUnlocked,
  useStatsVnoReservoirUnlocked,
} from '@ui/hooks/stats/useStatsVnoUnlocks';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useTranslations } from '@ui/i18n';
import { endOfDay, startOfDay } from 'date-fns';

export type StatsChartPage = 'vno' | 'staking';

const statsTvlEnabled = isFeatureEnabled(features.statsTvl);

export const useStatsCharts = (
  fromDate: Date,
  toDate: Date,
  page: StatsChartPage,
): StatsChartProps[] => {
  const startTime = Math.floor(startOfDay(fromDate).getTime() / 1000);
  const endTime = Math.floor(endOfDay(toDate).getTime() / 1000);
  const t = useTranslations();

  const { totalEarning, gardenEarning, fountainEarning } = useStatsClaimedVno(
    startTime,
    endTime,
  );

  const { atomStakers, croStakers } =
    useStatsStakers(startTime, endTime, { enabled: page === 'staking' }).data ??
    {};

  const { withdrawEarlyCounter, penaltyClaimedCounter } =
    useStatsFountainPenalties(startTime, endTime, { enabled: page === 'vno' })
      .data ?? {};

  const { ferroCroUsd, tectonicCroUsd, ferroAtomUsd, vvsCroUsd } =
    useStatsTvlUsd(startTime, endTime, {
      enabled: page === 'vno' && statsTvlEnabled,
    }).data ?? {};

  const { ferroCro, tectonicCro, ferroAtom, vvsCro, vvsVno } =
    useStatsTvlTokens(startTime, endTime, {
      enabled: page === 'vno' && statsTvlEnabled,
    }).data ?? {};

  const {
    pid0ReservoirVnoLocked,
    pid1ReservoirVnoLocked,
    pid2ReservoirVnoLocked,
    pid3ReservoirVnoLocked,
  } =
    useStatsReservoirVnoLocked(startTime, endTime, { enabled: page === 'vno' })
      .data ?? {};

  const {
    pid0FountainVnoLocked,
    pid1FountainVnoLocked,
    pid2FountainVnoLocked,
    pid3FountainVnoLocked,
  } =
    useStatsFountainVnoLocked(startTime, endTime, { enabled: page === 'vno' })
      .data ?? {};

  const {
    pid0FountainUsdLocked,
    pid1FountainUsdLocked,
    pid2FountainUsdLocked,
    pid3FountainUsdLocked,
  } =
    useStatsFountainUsdLocked(startTime, endTime, { enabled: page === 'vno' })
      .data ?? {};

  const {
    pid0ReservoirUsdLocked,
    pid1ReservoirUsdLocked,
    pid2ReservoirUsdLocked,
    pid3ReservoirUsdLocked,
  } =
    useStatsReservoirUsdLocked(startTime, endTime, { enabled: page === 'vno' })
      .data ?? {};

  const { vnoUnlocked: vnoFountainUnlocked } =
    useStatsVnoFountainUnlocked({ enabled: page === 'vno' }).data ?? {};
  const { vnoUnlocked: vnoReservoirUnlocked } =
    useStatsVnoReservoirUnlocked({ enabled: page === 'vno' }).data ?? {};

  const { croTvl, croTvlUsd, atomTvl, atomTvlUsd } =
    useStatsStakingTvl(startTime, endTime, { enabled: page === 'staking' })
      .data ?? {};
  const { croRewards, croRewardsUsd, atomRewards, atomRewardsUsd } =
    useStatsRewardsPaid(startTime, endTime, { enabled: page === 'staking' })
      .data ?? {};

  const { minerMoleEmployed, cronosCruiserEmployed, boomerSquadEmployed } =
    useStatsHelpersByCollection(startTime, endTime, { enabled: page === 'vno' })
      .data ?? {};

  const {
    croVolume,
    stakeCro,
    unstakeCro,
    atomVolume,
    stakeAtom,
    unstakeAtom,
  } =
    useStatsAverageDailyVolume(startTime, endTime, {
      enabled: page === 'staking',
    }).data ?? {};

  const {
    totalActive,
    stakeActiveCro,
    unstakeActiveAtom,
    unstakeActiveCro,
    stakeActiveAtom,
  } =
    useStatsDailyActiveWallets(startTime, endTime, {
      enabled: page === 'staking',
    }).data ?? {};

  const { stakeSizeAtom, stakeSizeCro, unstakeSizeAtom, unstakeSizeCro } =
    useStatsAverageStakeSize(startTime, endTime, {
      enabled: page === 'staking',
    }).data ?? {};

  const {
    pid1HelpersEmployed,
    pid2HelpersEmployed,
    pid3HelpersEmployed,
    pid4HelpersEmployed,
    pid5HelpersEmployed,
  } =
    useStatsHelpersByGarden(startTime, endTime, { enabled: page === 'vno' })
      .data ?? {};

  const stats: StatsChartProps[] =
    page === 'vno' && statsTvlEnabled
      ? [
          {
            title: t('VNO Earnings'),
            values: {
              [t('totalEarning')]: totalEarning,
              [t('gardenEarning')]: gardenEarning,
              [t('fountainEarning')]: fountainEarning,
            },
            type: 'line',
          },
          {
            title: t('Fountain Penalties'),
            values: {
              [t('withdrawEarlyCounter')]: withdrawEarlyCounter,
              [t('penaltyClaimedCounter')]: penaltyClaimedCounter,
            },
            type: 'line',
          },
          {
            title: t('Garden TVL in USD'),
            values: {
              'Ferro CRO USD': ferroCroUsd,
              'Tectonic CRO USD': tectonicCroUsd,
              'Ferro ATOM USD': ferroAtomUsd,
              'VVS CRO USD': vvsCroUsd,
            },
            type: 'line',
          },
          {
            title: t('Garden TVL in Tokens'),
            values: {
              'Ferro CRO': ferroCro,
              'Tectonic CRO': tectonicCro,
              'Ferro ATOM': ferroAtom,
              'VVS CRO': vvsCro,
              'VVS VNO': vvsVno,
            },
            type: 'line',
          },
          {
            title: t('Reservoir Total VNO Locked'),
            values: {
              [t('{num} Months', {
                num: 3,
              })]: pid0ReservoirVnoLocked,
              [t('{num} Months', {
                num: 12,
              })]: pid1ReservoirVnoLocked,
              [t('{num} Months', {
                num: 48,
              })]: pid2ReservoirVnoLocked,
              [t('{num} Months', {
                num: 96,
              })]: pid3ReservoirVnoLocked,
            },
            type: 'pie',
            units: ['VNO', 'USD'],
            alternativeValues: {
              [t('{num} Months', {
                num: 3,
              })]: pid0ReservoirUsdLocked,
              [t('{num} Months', {
                num: 12,
              })]: pid1ReservoirUsdLocked,
              [t('{num} Months', {
                num: 48,
              })]: pid2ReservoirUsdLocked,
              [t('{num} Months', {
                num: 96,
              })]: pid3ReservoirUsdLocked,
            },
          },
          {
            title: t('Fountain Total VNO Locked'),
            values: {
              [t('{num} Months', {
                num: 3,
              })]: pid0FountainVnoLocked,
              [t('{num} Months', {
                num: 12,
              })]: pid1FountainVnoLocked,
              [t('{num} Months', {
                num: 48,
              })]: pid2FountainVnoLocked,
              [t('{num} Months', {
                num: 96,
              })]: pid3FountainVnoLocked,
            },
            type: 'pie',
            alternativeValues: {
              [t('{num} Months', {
                num: 3,
              })]: pid0FountainUsdLocked,
              [t('{num} Months', {
                num: 12,
              })]: pid1FountainUsdLocked,
              [t('{num} Months', {
                num: 48,
              })]: pid2FountainUsdLocked,
              [t('{num} Months', {
                num: 96,
              })]: pid3FountainUsdLocked,
            },
            units: ['VNO', 'USD'],
          },
          {
            title: t('Reservoir VNO Unlocks'),
            values: {
              [t('Unlocked {coin}', {
                coin: 'VNO',
              })]: vnoReservoirUnlocked,
            },
            type: 'bar',
          },
          {
            title: t('Fountain VNO Unlocks'),
            values: {
              [t('Unlocked {coin}', {
                coin: 'VNO',
              })]: vnoFountainUnlocked,
            },
            type: 'bar',
          },
          {
            title: t('Total Helpers Employed by Collection'),
            values: {
              'VVS Miner Mole': minerMoleEmployed,
              'Cronos Cruisers': cronosCruiserEmployed,
              'Boomer Squad': boomerSquadEmployed,
            },
            type: 'line',
          },
          {
            title: t('Total Helpers Employed per Garden'),
            values: {
              'Ferro (CRO)': pid1HelpersEmployed,
              'VVS (VNO)': pid2HelpersEmployed,
              Tectonic: pid3HelpersEmployed,
              'Ferro (ATOM)': pid4HelpersEmployed,
              'VVS (CRO)': pid5HelpersEmployed,
            },
            type: 'line',
          },
        ]
      : page === 'vno' && !statsTvlEnabled
      ? [
          {
            title: t('VNO Earnings'),
            values: {
              [t('totalEarning')]: totalEarning,
              [t('gardenEarning')]: gardenEarning,
              [t('fountainEarning')]: fountainEarning,
            },
            type: 'line',
          },
          {
            title: t('Fountain Penalties'),
            values: {
              [t('withdrawEarlyCounter')]: withdrawEarlyCounter,
              [t('penaltyClaimedCounter')]: penaltyClaimedCounter,
            },
            type: 'line',
          },
          {
            title: t('Reservoir Total VNO Locked'),
            values: {
              [t('{num} Months', {
                num: 3,
              })]: pid0ReservoirVnoLocked,
              [t('{num} Months', {
                num: 12,
              })]: pid1ReservoirVnoLocked,
              [t('{num} Months', {
                num: 48,
              })]: pid2ReservoirVnoLocked,
              [t('{num} Months', {
                num: 96,
              })]: pid3ReservoirVnoLocked,
            },
            type: 'pie',
            units: ['VNO', 'USD'],
            alternativeValues: {
              [t('{num} Months', {
                num: 3,
              })]: pid0ReservoirUsdLocked,
              [t('{num} Months', {
                num: 12,
              })]: pid1ReservoirUsdLocked,
              [t('{num} Months', {
                num: 48,
              })]: pid2ReservoirUsdLocked,
              [t('{num} Months', {
                num: 96,
              })]: pid3ReservoirUsdLocked,
            },
          },
          {
            title: t('Fountain Total VNO Locked'),
            values: {
              [t('{num} Months', {
                num: 3,
              })]: pid0FountainVnoLocked,
              [t('{num} Months', {
                num: 12,
              })]: pid1FountainVnoLocked,
              [t('{num} Months', {
                num: 48,
              })]: pid2FountainVnoLocked,
              [t('{num} Months', {
                num: 96,
              })]: pid3FountainVnoLocked,
            },
            type: 'pie',
            alternativeValues: {
              [t('{num} Months', {
                num: 3,
              })]: pid0FountainUsdLocked,
              [t('{num} Months', {
                num: 12,
              })]: pid1FountainUsdLocked,
              [t('{num} Months', {
                num: 48,
              })]: pid2FountainUsdLocked,
              [t('{num} Months', {
                num: 96,
              })]: pid3FountainUsdLocked,
            },
            units: ['VNO', 'USD'],
          },
          {
            title: t('Reservoir VNO Unlocks'),
            values: {
              [t('Unlocked {coin}', {
                coin: 'VNO',
              })]: vnoReservoirUnlocked,
            },
            type: 'bar',
          },
          {
            title: t('Fountain VNO Unlocks'),
            values: {
              [t('Unlocked {coin}', {
                coin: 'VNO',
              })]: vnoFountainUnlocked,
            },
            type: 'bar',
          },
          {
            title: t('Total Helpers Employed by Collection'),
            values: {
              'VVS Miner Mole': minerMoleEmployed,
              'Cronos Cruisers': cronosCruiserEmployed,
              'Boomer Squad': boomerSquadEmployed,
            },
            type: 'line',
          },
          {
            title: t('Total Helpers Employed per Garden'),
            values: {
              'Ferro (CRO)': pid1HelpersEmployed,
              'VVS (VNO)': pid2HelpersEmployed,
              Tectonic: pid3HelpersEmployed,
              'Ferro (ATOM)': pid4HelpersEmployed,
              'VVS (CRO)': pid5HelpersEmployed,
            },
            type: 'line',
          },
        ]
      : [
          {
            title: t('Stakers'),
            values: {
              [t('{coin} Stakers', {
                coin: 'ATOM',
              })]: atomStakers,
              [t('{coin} Stakers', {
                coin: 'CRO',
              })]: croStakers,
            },
            type: 'line',
          },
          {
            title: t('Staking TVL'),
            values: {
              'CRO TVL': croTvl,
              'CRO TVL USD': croTvlUsd,
              'ATOM TVL': atomTvl,
              'ATOM TVL USD': atomTvlUsd,
            },
            type: 'line',
          },
          {
            title: t('Rewards Paid'),
            values: {
              [t('{coin} rewards', {
                coin: 'CRO',
              })]: croRewards,
              [t('{coin} rewards USD', {
                coin: 'CRO',
              })]: croRewardsUsd,
              [t('{coin} rewards', {
                coin: 'ATOM',
              })]: atomRewards,
              [t('{coin} rewards USD', {
                coin: 'ATOM',
              })]: atomRewardsUsd,
            },
            type: 'line',
          },
          {
            title: t('Average Daily Staking Volume'),
            values: {
              [t('{coin} volume', { coin: 'CRO' })]: croVolume,
              [t('{coin} volume', { coin: 'ATOM' })]: atomVolume,
              [t('Stake {coin}', { coin: 'CRO' })]: stakeCro,
              [t('Stake {coin}', { coin: 'ATOM' })]: stakeAtom,
              [t('Unstake {coin}', { coin: 'CRO' })]: unstakeCro,
              [t('Unstake {coin}', { coin: 'ATOM' })]: unstakeAtom,
            },
            type: 'bar',
          },
          {
            title: t('Daily Active Wallets'),
            values: {
              [t('Total Stake & Unstake')]: totalActive,
              [t('Stake {coin}', { coin: 'CRO' })]: stakeActiveCro,
              [t('Stake {coin}', { coin: 'ATOM' })]: stakeActiveAtom,
              [t('Unstake {coin}', { coin: 'CRO' })]: unstakeActiveCro,
              [t('Unstake {coin}', { coin: 'ATOM' })]: unstakeActiveAtom,
            },
            type: 'bar',
          },
          {
            title: t('Average Stake Size'),
            values: {
              [t('Stake {coin}', { coin: 'CRO' })]: stakeSizeCro,
              [t('Stake {coin}', { coin: 'ATOM' })]: stakeSizeAtom,
              [t('Unstake {coin}', { coin: 'CRO' })]: unstakeSizeCro,
              [t('Unstake {coin}', { coin: 'ATOM' })]: unstakeSizeAtom,
            },
            type: 'bar',
          },
        ];

  return stats;
};
